import React from "react";
import ReactDOM from "react-dom";
import { createBrowserHistory as createHistory } from "history";

import { Amplify, Auth } from "aws-amplify";
// Import Geo before Amplify.configure so it self-registers and receives the geo config
import { Geo } from "@aws-amplify/geo";

import awsExports from "./aws-exports";
import * as serviceWorker from "./serviceWorker";

import App from "./App";
import { UserProvider } from "./authenticatorApp/UserContext";
import { AuthProvider } from "react-oidc-context";
import { WebStorageStateStore } from "oidc-client-ts";
import { getOauthConfig } from "./utils/authUtils";
import { ThemeStateProvider } from "./ThemeStateProvider";

Amplify.configure({
  ...awsExports,
  oauth: {
    ...getOauthConfig(),
  },
});

// @aws-amplify/core@4.2.9's mobile-hub parser doesn't translate aws-exports' `geo`
// block, and Amplify.configure passes the whole merged config (not the Geo subtree)
// to Geo.configure — leaving the provider with no search index / maps. Configure
// the Geo singleton directly under the provider's name ("AmazonLocationService").
Geo.configure({
  AmazonLocationService: awsExports.geo && awsExports.geo.amazon_location_service,
});

const adminHistory = createHistory({ basename: "/" });

// Public driver gate wizard (docs/plans/2026-07-18-driver-app.md AD-13):
// /d/<yardId> renders WITHOUT AuthProvider/UserProvider/App so the OIDC
// redirect in App.js never fires for drivers. Lazy: keeps react-admin and
// the portals out of the driver chunk (fast boot on old gate tablets).
const PublicDriverApp = React.lazy(() => import("./driverApp/PublicDriverApp"));
const isDriverGate =
  window.location.pathname === "/d" || window.location.pathname.startsWith("/d/");

const cognitoAuthConfig = {
  authority: `https://cognito-idp.${awsExports.aws_cognito_region}.amazonaws.com/${awsExports.aws_user_pools_id}`,
  client_id: awsExports.aws_user_pools_web_client_id,
  redirect_uri: window.location.origin,
  response_type: Auth._config.oauth.responseType,
  scope: Auth._config.oauth.scope.join(" "),
  automaticSilentRenew: true, // Automatically refresh token before it expires
  loadUserInfo: true,
  // Share tokens across tabs so a refresh in one tab is visible to all others
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};

ReactDOM.render(
  <React.StrictMode>
    {isDriverGate ? (
      <ThemeStateProvider>
        <React.Suspense fallback={null}>
          <PublicDriverApp />
        </React.Suspense>
      </ThemeStateProvider>
    ) : (
      <AuthProvider {...cognitoAuthConfig}>
        <UserProvider>
          <ThemeStateProvider>
            <App adminHistory={adminHistory} />
          </ThemeStateProvider>
        </UserProvider>
      </AuthProvider>
    )}
  </React.StrictMode>,
  document.getElementById("root"),
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
